// packages
const { h, Component } = require('preact');
import { select, scaleBand, scaleLinear, axisLeft } from 'd3';
import { formatNumberPct, formatYAxisTicks, formatNumberWithCommas } from '../utils/utils';

const COLORS = {
  dem: '#1755c5',
  gop: '#f2593b',
};

const CATEGORIES = ['dem', 'gop'];

class RGVPartyBarChart extends Component {
  constructor() {
    super();
    this.state = {};
    this.drawChart = this.drawChart.bind(this);
    this.redrawChart = this.redrawChart.bind(this);
  }

  drawChart() {
    const { yearData, maxValue, showYAxis } = this.props;
    const container = select(`.rgv-bar-container-${yearData.year}`);
    if (!container.node()) return;

    const width = container.node().clientWidth;
    const margin = { t: 20, r: 5, b: 45, l: 40 };
    const chartWidth = width - margin.l - margin.r;
    const chartHeight = 180;
    const height = chartHeight + margin.t + margin.b;

    const xScale = scaleBand()
      .domain(CATEGORIES)
      .range([0, chartWidth])
      .padding(0.15);

    const yScale = scaleLinear()
      .domain([0, maxValue])
      .range([chartHeight, 0]);

    const svg = select(`.rgv-bar-container-${yearData.year}`)
      .append('svg')
      .attr('width', width)
      .attr('height', height)
      .append('g')
      .attr('transform', `translate(${margin.l},${margin.t})`);

    // y-axis
    const yAxisG = svg.append('g')
      .attr('class', 'axis axis--y')
      .call(
        axisLeft(yScale)
          .tickValues([0, 25000, 50000, 75000, 100000])
          .tickSize(-chartWidth)
          .tickFormat(d => {
            if (!showYAxis) return '';
            if (d === 100000) return '100K';
            return formatYAxisTicks(d);
          })
      );

    yAxisG.selectAll('text')
      .style('font-size', '10px');

    if (showYAxis) {
      yAxisG.select('.tick:last-of-type text')
        .append('tspan')
        .attr('x', 0)
        .attr('dy', '1.1em')
        .text('voters');
    }

    // bars
    const grouped = CATEGORIES.map(cat => ({ category: cat, value: +yearData[cat] }));

    svg.selectAll('.rgv-bar')
      .data(grouped)
      .enter()
      .append('rect')
      .attr('class', 'rgv-bar')
      .attr('x', d => xScale(d.category))
      .attr('y', d => yScale(d.value))
      .attr('width', xScale.bandwidth())
      .attr('height', d => chartHeight - yScale(d.value))
      .attr('fill', d => COLORS[d.category]);

    // percentage labels above bars
    svg.selectAll('.pct-label')
      .data(grouped)
      .enter()
      .append('text')
      .attr('class', 'pct-label')
      .attr('x', d => xScale(d.category) + xScale.bandwidth() / 2)
      .attr('y', d => yScale(d.value) - 4)
      .attr('text-anchor', 'middle')
      .style('font-size', '10px')
      .style('font-weight', 'normal')
      .text(d => formatNumberPct(d.value / yearData.registered * 100));

    // party labels below x-axis
    const labelG = svg.append('g').attr('transform', `translate(0,${chartHeight})`);

    const LABELS = { dem: 'D', gop: 'R' };
    CATEGORIES.forEach(cat => {
      const cx = xScale(cat) + xScale.bandwidth() / 2;
      const g = labelG.append('g').attr('transform', `translate(${cx},16)`);
      g.append('circle')
        .attr('r', 8)
        .attr('fill', COLORS[cat]);
      g.append('text')
        .attr('text-anchor', 'middle')
        .attr('dy', 4)
        .attr('fill', 'white')
        .style('font-weight', 'bold')
        .style('font-size', '10px')
        .text(LABELS[cat]);
    });
  }

  redrawChart() {
    select(`.rgv-bar-container-${this.props.yearData.year}`)
      .select('svg')
      .remove();
    this.drawChart();
  }

  componentDidMount() {
    this.drawChart();
    const containerEl = document.querySelector(`.rgv-bar-container-${this.props.yearData.year}`);
    if (containerEl && window.ResizeObserver) {
      this._resizeObserver = new ResizeObserver(() => this.redrawChart());
      this._resizeObserver.observe(containerEl);
    } else {
      this._resizeHandler = () => this.redrawChart();
      window.addEventListener('resize', this._resizeHandler);
    }
  }

  componentWillUnmount() {
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
    } else if (this._resizeHandler) {
      window.removeEventListener('resize', this._resizeHandler);
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.yearData !== this.props.yearData) {
      this.redrawChart();
    }
  }

  render() {
    const { yearData } = this.props;
    const totalVotes = yearData.dem + yearData.gop;
    const totalPct = formatNumberPct(totalVotes / yearData.registered * 100);
    return (
      <div class="party-chart-wrapper">
        <div class="party-chart-header">
          <p class="party-chart-label">{yearData.label} primary</p>
          <h2 class="party-chart-year">{yearData.year}</h2>
          <p class="party-chart-total">{totalPct} of {formatNumberWithCommas(yearData.registered)} registered voters</p>
        </div>
        <div class={`rgv-bar-container-${yearData.year}`}></div>
      </div>
    );
  }
}

export default RGVPartyBarChart;
